import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@/app/store";
import { APIResponse } from "@/types/custom-http";
import {
  CertificationDetails,
  CertificationFilters,
  CertificationPagination,
  CertificationView,
  CertificationsState,
  CertificationModalsState,
} from "./certifications.interface";
import * as thunks from "./certificationsThunks";

const initialState: CertificationsState = {
  items: [],
  selectedCertification: null,
  selectedCertificationId: null,
  selectedWorkOrderId: null,
  filters: {
    state_id: null,
    kind_id: null,
    period_start: null,
    period_end: null,
  },
  pagination: {
    page: 1,
    size: 10,
    total: 0,
    totalPages: 0,
  },
  modals: {
    isCreateOpen: false,
    isEditOpen: false,
    isApproveOpen: false,
    isRejectOpen: false,
    isVoidOpen: false,
  },
  loading: false,
  error: null,
};

const certificationsSlice = createSlice({
  name: "certifications",
  initialState,
  reducers: {
    setSelectedCertificationId: (state, action: PayloadAction<number | null>) => {
      state.selectedCertificationId = action.payload;
    },
    setSelectedWorkOrderId: (state, action: PayloadAction<number | null>) => {
      state.selectedWorkOrderId = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<CertificationFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setPagination: (state, action: PayloadAction<Partial<CertificationPagination>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setModalsState: (state, action: PayloadAction<Partial<CertificationModalsState>>) => {
      state.modals = { ...state.modals, ...action.payload };
    },
    clearSelectedCertification: (state) => {
      state.selectedCertification = null;
      state.selectedCertificationId = null;
    },
    clearCertifications: (state) => {
      state.items = [];
    },
    clearCertificationsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(thunks.fetchCertificationsByWorkOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchCertificationsByWorkOrder.fulfilled, (state, action: PayloadAction<APIResponse<CertificationView[]>>) => {
        state.loading = false;
        if (action.payload.data) {
          state.items = action.payload.data;
        }
      })
      .addCase(thunks.fetchCertificationsByWorkOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      .addCase(thunks.fetchCertificationById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchCertificationById.fulfilled, (state, action: PayloadAction<APIResponse<CertificationDetails>>) => {
        state.loading = false;
        if (action.payload.data) {
          state.selectedCertification = action.payload.data;
          state.selectedCertificationId = action.payload.data.certificacion.id;
        }
      })
      .addCase(thunks.fetchCertificationById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      .addCase(thunks.createCertification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.createCertification.fulfilled, (state, action: PayloadAction<APIResponse<CertificationDetails>>) => {
        state.loading = false;
        if (action.payload.data?.certificacion) {
          state.items.push(action.payload.data.certificacion);
          state.selectedCertification = action.payload.data;
          state.selectedCertificationId = action.payload.data.certificacion.id;
        }
      })
      .addCase(thunks.createCertification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      .addCase(thunks.updateCertificationDraft.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.updateCertificationDraft.fulfilled, (state, action: PayloadAction<APIResponse<CertificationDetails>>) => {
        state.loading = false;
        if (action.payload.data?.certificacion) {
          const updated = action.payload.data.certificacion;
          const index = state.items.findIndex((item) => item.id === updated.id);
          if (index !== -1) {
            state.items[index] = updated;
          }
          state.selectedCertification = action.payload.data;
        }
      })
      .addCase(thunks.updateCertificationDraft.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      .addCase(thunks.deleteCertificationDraft.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.deleteCertificationDraft.fulfilled, (state) => {
        state.loading = false;
        if (state.selectedCertificationId) {
          state.items = state.items.filter((item) => item.id !== state.selectedCertificationId);
          state.selectedCertification = null;
          state.selectedCertificationId = null;
        }
      })
      .addCase(thunks.deleteCertificationDraft.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      .addCase(thunks.submitCertification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.submitCertification.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(thunks.submitCertification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      .addCase(thunks.approveCertification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.approveCertification.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(thunks.approveCertification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      .addCase(thunks.rejectCertification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.rejectCertification.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(thunks.rejectCertification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      .addCase(thunks.voidCertification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.voidCertification.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(thunks.voidCertification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      });
  },
});

export const selectAllCertifications = (state: RootState) => state.certifications.items;
export const selectSelectedCertification = (state: RootState) => state.certifications.selectedCertification;
export const selectSelectedCertificationId = (state: RootState) => state.certifications.selectedCertificationId;
export const selectSelectedWorkOrderId = (state: RootState) => state.certifications.selectedWorkOrderId;
export const selectCertificationFilters = (state: RootState) => state.certifications.filters;
export const selectCertificationPagination = (state: RootState) => state.certifications.pagination;
export const selectCertificationModals = (state: RootState) => state.certifications.modals;
export const selectCertificationsLoading = (state: RootState) => state.certifications.loading;
export const selectCertificationsError = (state: RootState) => state.certifications.error;

export const {
  setSelectedCertificationId,
  setSelectedWorkOrderId,
  setFilters,
  setPagination,
  setModalsState,
  clearSelectedCertification,
  clearCertifications,
  clearCertificationsError,
} = certificationsSlice.actions;

export default certificationsSlice.reducer;
